import React from "react";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { normalizeDispatchAlertCaptureContext } from "../lib/dispatchAlertCaptureContext.js";
import { appendDispatchDateToUrl, buildResultsLatestApiInvalidationUrls } from "../utils/dispatchDateUrls.js";
import {
  mergeOptimizationProgress,
  sanitizeOptimizationProgress,
  sanitizeOptimizationProgressForStatus,
} from "../utils/optimizationProgress.js";
import { invalidatePageDataCacheEntries } from "./usePageData.js";
import { invalidateShellDataCache } from "./useShellData.js";

const OPTIMIZATION_JOB_STORAGE_KEY = "grain-saas:optimization-job";
const DEFAULT_RESULT_URL = "/results";
const DEFAULT_POLL_URL = "/api/optimization/status/current";
const DEFAULT_CANCEL_URL = "/api/optimization/status/current/cancel";
const DEFAULT_POLL_INTERVAL_MS = 2000;
export const OPTIMIZATION_CANCELLING_POLL_INTERVAL_MS = 500;
const OPTIMIZATION_PREVIEW_MODES = new Set(["off", "boundary", "solution"]);
const OPTIMIZATION_TRACKED_STATUSES = new Set([
  "running",
  "cancelling",
  "cancelled",
  "completed",
  "failed",
]);
const OPTIMIZATION_COMPLETION_INVALIDATED_API_URLS = [
  "/api/dashboard",
  "/api/dashboard/home",
];
const PLANNING_RESET_COMPLETION_INVALIDATED_API_URLS = ["/api/data-manager/state"];
export const OPTIMIZATION_STATUS_POLL_RETRY_LIMIT = 3;

const OptimizationJobTrackerContext = React.createContext(null);

export function isOptimizationPollingEnabledForPathname(pathname) {
  return pathname !== "/accueil";
}

export function buildOptimizationCancelUrl(jobId) {
  const resolvedJobId = typeof jobId === "string" ? jobId.trim() : "";
  return resolvedJobId
    ? `/api/optimization/status/${encodeURIComponent(resolvedJobId)}/cancel`
    : DEFAULT_CANCEL_URL;
}

function getDefaultResultUrl() {
  return appendDispatchDateToUrl(DEFAULT_RESULT_URL);
}

function sanitizeOptimizationJobServiceHealth(candidate) {
  return (Array.isArray(candidate) ? candidate : []).reduce((items, item) => {
    if (!item || typeof item !== "object") {
      return items;
    }

    items.push({
      key: typeof item.key === "string" ? item.key : "",
      label: typeof item.label === "string" ? item.label : "",
      status: typeof item.status === "string" ? item.status : "unknown",
      summary: typeof item.summary === "string" ? item.summary : "",
      detail: typeof item.detail === "string" ? item.detail : "",
      signals: Array.isArray(item.signals) ? item.signals.filter((signal) => typeof signal === "string") : [],
    });
    return items;
  }, []);
}

function sanitizeOptimizationJobDetailItems(candidate) {
  return (Array.isArray(candidate) ? candidate : []).reduce((items, item, index) => {
    if (!item || typeof item !== "object") {
      return items;
    }

    const label = typeof item.label === "string" ? item.label.trim() : "";
    const value = typeof item.value === "string" ? item.value.trim() : "";
    if (!label || !value) {
      return items;
    }

    items.push({
      key: typeof item.key === "string" && item.key.trim() ? item.key.trim() : `detail-${index + 1}`,
      label,
      value,
    });
    return items;
  }, []);
}

function sanitizeOptimizationJobIssueValues(candidate) {
  return (Array.isArray(candidate) ? candidate : []).reduce((items, item, index) => {
    if (!item || typeof item !== "object") {
      return items;
    }

    const label = typeof item.label === "string" ? item.label.trim() : "";
    const value = typeof item.value === "string" ? item.value.trim() : "";
    if (!label || !value) {
      return items;
    }

    items.push({
      key: typeof item.key === "string" && item.key.trim() ? item.key.trim() : `issue-value-${index + 1}`,
      label,
      value,
    });
    return items;
  }, []);
}

function sanitizeOptimizationJobIssueItems(candidate) {
  return (Array.isArray(candidate) ? candidate : []).reduce((items, item, index) => {
    if (!item || typeof item !== "object") {
      return items;
    }

    const title = typeof item.title === "string" ? item.title.trim() : "";
    if (!title) {
      return items;
    }

    items.push({
      key: typeof item.key === "string" && item.key.trim() ? item.key.trim() : `issue-${index + 1}`,
      orderId: typeof item.orderId === "string" && item.orderId.trim()
        ? item.orderId.trim()
        : (typeof item.order_id === "string" && item.order_id.trim() ? item.order_id.trim() : null),
      summary: typeof item.summary === "string" && item.summary.trim() ? item.summary.trim() : null,
      title,
      values: sanitizeOptimizationJobIssueValues(item.values),
    });
    return items;
  }, []);
}

function sanitizeOptimizationJobDiagnostics(candidate) {
  const rawDiagnostics = candidate?.optimizationDiagnostics
    || candidate?.optimization_diagnostics
    || candidate?.diagnostics
    || candidate?.payload?.optimization_diagnostics
    || candidate?.payload?.diagnostics
    || candidate?.payload?.result_payload?.optimization_diagnostics
    || null;

  return rawDiagnostics && typeof rawDiagnostics === "object" && !Array.isArray(rawDiagnostics)
    ? rawDiagnostics
    : null;
}

function sanitizeOptimizationJobErrorDetails(candidate) {
  const details = candidate?.errorDetails ?? candidate?.error_details;
  return details && typeof details === "object" && !Array.isArray(details) ? details : null;
}

function hasTrackedOptimizationDiagnostics(candidate) {
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return false;
  }

  if (
    Object.prototype.hasOwnProperty.call(candidate, "optimizationDiagnostics")
    || Object.prototype.hasOwnProperty.call(candidate, "optimization_diagnostics")
    || Object.prototype.hasOwnProperty.call(candidate, "diagnostics")
  ) {
    return true;
  }

  if (candidate.payload && typeof candidate.payload === "object" && !Array.isArray(candidate.payload)) {
    return hasTrackedOptimizationDiagnostics(candidate.payload);
  }

  return false;
}

function normalizeOptionalNonnegativeInt(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }

  return Math.round(numericValue);
}

function normalizeOptionalDurationMs(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }

  return Math.round(numericValue * 10) / 10;
}

function normalizeOptionalEpochMs(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }

  return Math.round(numericValue);
}

function sanitizeOptionalPlainObject(candidate) {
  return candidate && typeof candidate === "object" && !Array.isArray(candidate)
    ? candidate
    : null;
}

function sanitizeOptionalAssignmentPreview(candidate) {
  return sanitizeOptionalPlainObject(candidate);
}

function readOptionalOptimizationField(candidate, camelCaseKey, snakeCaseKey) {
  if (!candidate || typeof candidate !== "object") {
    return { present: false, value: undefined };
  }

  if (Object.prototype.hasOwnProperty.call(candidate, camelCaseKey)) {
    return { present: true, value: candidate[camelCaseKey] };
  }

  if (Object.prototype.hasOwnProperty.call(candidate, snakeCaseKey)) {
    return { present: true, value: candidate[snakeCaseKey] };
  }

  return { present: false, value: undefined };
}

function resolveOptionalOptimizationPreview(candidate, fallback = null) {
  const field = readOptionalOptimizationField(candidate, "assignmentPreview", "assignment_preview");
  return field.present ? sanitizeOptionalAssignmentPreview(field.value) : fallback;
}

function normalizeOptionalOptimizationPreviewCursor(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  // Cursors are opaque to the client. Keep numeric cursors numeric for
  // comparisons, while accepting string cursors from older API deployments.
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return Math.round(value);
  }
  if (typeof value === "string" && value.trim()) {
    const normalized = value.trim();
    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) && numericValue >= 0
      ? Math.round(numericValue)
      : normalized;
  }
  return null;
}

function readOptimizationPreviewEventPreview(candidate) {
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return null;
  }

  const nestedCandidates = [
    candidate.assignmentPreview,
    candidate.assignment_preview,
    candidate.preview,
    candidate.payload?.assignmentPreview,
    candidate.payload?.assignment_preview,
    candidate.payload?.preview,
  ];
  const nestedPreview = nestedCandidates.find((value) => (
    value && typeof value === "object" && !Array.isArray(value)
  ));
  return sanitizeOptionalAssignmentPreview(nestedPreview || candidate);
}

function readOptimizationPreviewEventCursor(candidate, preview = null, fallback = null) {
  const values = [
    // v2 events are previews themselves; `revision` is the authoritative
    // cursor.  Keep the nested aliases below only for older response shapes.
    candidate?.revision,
    candidate?.payload?.revision,
    preview?.revision,
    candidate?.assignmentPreviewCursor,
    candidate?.assignment_preview_cursor,
    candidate?.previewCursor,
    candidate?.preview_cursor,
    candidate?.sequence,
    candidate?.offset,
    candidate?.payload?.cursor,
    candidate?.cursor,
    preview?.cursor,
    preview?.assignment_preview_cursor,
    preview?.preview_cursor,
  ];
  for (const value of values) {
    const cursor = normalizeOptionalOptimizationPreviewCursor(value);
    if (cursor !== null) {
      return cursor;
    }
  }
  return normalizeOptionalOptimizationPreviewCursor(fallback);
}

function readOptimizationPreviewEventId(candidate, preview = null, fallback = "") {
  const values = [
    candidate?.eventId,
    candidate?.event_id,
    candidate?.id,
    candidate?.payload?.eventId,
    candidate?.payload?.event_id,
    preview?.eventId,
    preview?.event_id,
  ];
  return values.find((value) => typeof value === "string" && value.trim())?.trim()
    || (typeof fallback === "string" ? fallback.trim() : "");
}

/**
 * Normalize one assignment-preview stream event while accepting the direct
 * preview shape used by the legacy `assignment_preview` field.
 */
export function sanitizeOptimizationPreviewEvent(candidate, index = 0) {
  const preview = readOptimizationPreviewEventPreview(candidate);
  if (!preview) {
    return null;
  }
  const cursor = readOptimizationPreviewEventCursor(candidate, preview);
  const eventId = readOptimizationPreviewEventId(candidate, preview);
  return {
    ...preview,
    cursor,
    eventId: eventId || null,
    eventIndex: index,
    preview,
  };
}

export function sanitizeOptimizationPreviewEvents(candidate) {
  const rawEvents = Array.isArray(candidate) ? candidate : [];
  return rawEvents
    .map((event, index) => sanitizeOptimizationPreviewEvent(event, index))
    .filter(Boolean)
    .sort((left, right) => {
      const leftCursor = Number(left.cursor);
      const rightCursor = Number(right.cursor);
      if (Number.isFinite(leftCursor) && Number.isFinite(rightCursor) && leftCursor !== rightCursor) {
        return leftCursor - rightCursor;
      }
      const leftRevision = Number(left.revision || left.preview?.revision || 0);
      const rightRevision = Number(right.revision || right.preview?.revision || 0);
      return leftRevision - rightRevision || left.eventIndex - right.eventIndex;
    });
}

export function resolveOptimizationPreviewEvents(payload) {
  const candidates = [
    payload?.assignmentPreviewEvents,
    payload?.assignment_preview_events,
    payload?.previewEvents,
    payload?.preview_events,
  ];
  const rawEvents = candidates.find((value) => Array.isArray(value));
  return sanitizeOptimizationPreviewEvents(rawEvents || []);
}

export function resolveOptimizationPreviewHasMore(payload, fallback = false) {
  const values = [
    payload?.assignmentPreviewHasMore,
    payload?.assignment_preview_has_more,
    payload?.previewHasMore,
    payload?.preview_has_more,
  ];
  const value = values.find((candidate) => typeof candidate === "boolean");
  return value === undefined ? fallback === true : value;
}

export function resolveOptimizationPreviewCursor(payload, fallback = null) {
  const responseEvents = resolveOptimizationPreviewEvents(payload);
  const lastResponseEvent = responseEvents[responseEvents.length - 1] || null;
  const eventCursor = normalizeOptionalOptimizationPreviewCursor(
    lastResponseEvent?.revision ?? lastResponseEvent?.cursor,
  );
  if (eventCursor !== null) {
    return eventCursor;
  }

  const values = [
    payload?.assignmentPreviewLastReceivedRevision,
    payload?.assignment_preview_last_received_revision,
    payload?.assignmentPreviewCursor,
    payload?.assignment_preview_cursor,
    payload?.previewCursor,
    payload?.preview_cursor,
    payload?.nextAssignmentPreviewCursor,
    payload?.next_assignment_preview_cursor,
    payload?.nextPreviewCursor,
    payload?.next_preview_cursor,
    payload?.nextCursor,
    payload?.next_cursor,
  ];
  for (const value of values) {
    const cursor = normalizeOptionalOptimizationPreviewCursor(value);
    if (cursor !== null) {
      return cursor;
    }
  }
  return normalizeOptionalOptimizationPreviewCursor(fallback);
}

export function resolveOptimizationPreviewLatestRevision(payload, fallback = 0) {
  const values = [
    payload?.assignmentPreviewLatestRevision,
    payload?.assignment_preview_latest_revision,
    payload?.latestPreviewRevision,
    payload?.latest_preview_revision,
  ];
  for (const value of values) {
    const revision = normalizeOptionalOptimizationPreviewCursor(value);
    if (revision !== null) {
      return revision;
    }
  }
  return normalizeOptionalOptimizationPreviewCursor(fallback) ?? 0;
}

function resolvePreviewEventKey(event) {
  const cursor = normalizeOptionalOptimizationPreviewCursor(event?.cursor);
  if (cursor !== null) {
    return `cursor:${String(cursor)}`;
  }
  const eventId = typeof event?.eventId === "string" ? event.eventId.trim() : "";
  if (eventId) {
    return `event:${eventId}`;
  }
  const revision = Number(event?.revision || event?.preview?.revision || 0);
  return revision > 0 ? `revision:${revision}` : "event:index";
}

export function resolveTrackedOptimizationPreviewState(payload, currentState = {}) {
  const payloadStatus = typeof payload?.status === "string"
    ? payload.status.trim().toLowerCase()
    : "";
  const previewField = readOptionalOptimizationField(
    payload,
    "assignmentPreview",
    "assignment_preview",
  );
  const eventsField = readOptionalOptimizationField(
    payload,
    "assignmentPreviewEvents",
    "assignment_preview_events",
  );
  const resultCommittedField = readOptionalOptimizationField(
    payload,
    "resultCommitted",
    "result_committed",
  );
  const terminalPreviewJournalWasDiscarded = ["cancelled", "failed"].includes(payloadStatus)
    && resultCommittedField.value !== true
    && previewField.present
    && sanitizeOptionalAssignmentPreview(previewField.value) === null
    && eventsField.present
    && sanitizeOptimizationPreviewEvents(eventsField.value).length === 0
    && resolveOptimizationPreviewLatestRevision(payload, 0) === 0;
  if (terminalPreviewJournalWasDiscarded) {
    return {
      assignmentPreview: null,
      assignmentPreviewCursor: 0,
      assignmentPreviewSnapshot: null,
      assignmentPreviewLastReceivedRevision: 0,
      assignmentPreviewLatestRevision: 0,
      assignmentPreviewEvents: [],
      assignmentPreviewHasMore: false,
    };
  }

  const currentPreview = currentState?.assignmentPreview || null;
  const currentSnapshot = currentState?.assignmentPreviewSnapshot || null;
  const currentCursor = normalizeOptionalOptimizationPreviewCursor(
    currentState?.assignmentPreviewLastReceivedRevision
      ?? currentState?.assignmentPreviewCursor,
  ) ?? 0;
  const currentCursorNumber = Number(currentCursor);
  const incomingEvents = resolveOptimizationPreviewEvents(payload);
  const seenKeys = new Set();
  const freshEvents = incomingEvents.filter((event) => {
    const key = resolvePreviewEventKey(event);
    if (seenKeys.has(key)) {
      return false;
    }
    seenKeys.add(key);
    const eventRevision = Number(event.revision || event.preview?.revision || event.cursor || 0);
    return !Number.isFinite(currentCursorNumber) || !eventRevision || eventRevision > currentCursorNumber;
  });

  const legacyPreview = resolveOptionalOptimizationPreview(payload);
  const hasEventStream = Object.prototype.hasOwnProperty.call(payload || {}, "assignment_preview_events")
    || Object.prototype.hasOwnProperty.call(payload || {}, "assignmentPreviewEvents");
  const latestSnapshot = legacyPreview || currentSnapshot;
  const normalizedLatestRevision = Math.max(
    Number(resolveOptimizationPreviewLatestRevision(payload, 0)) || 0,
    Number(latestSnapshot?.revision || 0) || 0,
    Number(currentState?.assignmentPreviewLatestRevision || 0) || 0,
    Number(currentCursor) || 0,
  );
  // `assignment_preview` is a latest snapshot, not the cursor. While a page of
  // events remains to be consumed it may be several revisions ahead; never
  // use that snapshot to discard the ordered events in front of it.
  if (!hasEventStream && !freshEvents.length && legacyPreview) {
    const legacyRevision = Number(legacyPreview.revision || 0);
    if (!legacyRevision || legacyRevision > currentCursorNumber) {
      freshEvents.push(sanitizeOptimizationPreviewEvent(legacyPreview, 0));
    }
  }

  const lastEvent = freshEvents[freshEvents.length - 1] || null;
  const eventCursor = lastEvent
    ? normalizeOptionalOptimizationPreviewCursor(lastEvent.revision || lastEvent.cursor)
    : null;
  const nextCursor = eventCursor !== null ? eventCursor : currentCursor;
  const shouldUseLatestSnapshot = !hasEventStream
    || (!resolveOptimizationPreviewHasMore(payload, currentState?.assignmentPreviewHasMore)
      && (!freshEvents.length || Number(latestSnapshot?.revision || 0) <= Number(nextCursor)));
  const nextPreview = lastEvent?.preview
    || (shouldUseLatestSnapshot ? latestSnapshot : currentPreview);
  return {
    assignmentPreview: nextPreview,
    assignmentPreviewCursor: nextCursor,
    assignmentPreviewSnapshot: latestSnapshot,
    assignmentPreviewLastReceivedRevision: nextCursor,
    assignmentPreviewLatestRevision: normalizedLatestRevision,
    assignmentPreviewEvents: freshEvents,
    assignmentPreviewHasMore: resolveOptimizationPreviewHasMore(
      payload,
      currentState?.assignmentPreviewHasMore,
    ),
  };
}

function resolveOptionalOptimizationCheckpoint(candidate, fallback = null) {
  const field = readOptionalOptimizationField(candidate, "checkpoint", "checkpoint");
  if (!field.present) {
    return fallback;
  }

  if (typeof field.value === "string" && field.value.trim()) {
    return field.value.trim();
  }

  return sanitizeOptionalPlainObject(field.value);
}

function resolveOptionalOptimizationBoolean(candidate, camelCaseKey, snakeCaseKey, fallback = false) {
  const field = readOptionalOptimizationField(candidate, camelCaseKey, snakeCaseKey);
  return field.present ? field.value === true : fallback;
}

function resolveOptionalOptimizationPreviewMode(candidate, fallback = "off") {
  const field = readOptionalOptimizationField(candidate, "previewMode", "preview_mode");
  const fallbackMode = typeof fallback === "string" ? fallback.trim().toLowerCase() : "off";
  const resolvedFallback = OPTIMIZATION_PREVIEW_MODES.has(fallbackMode) ? fallbackMode : "off";
  if (!field.present) {
    return resolvedFallback;
  }

  const resolvedMode = typeof field.value === "string" ? field.value.trim().toLowerCase() : "";
  return OPTIMIZATION_PREVIEW_MODES.has(resolvedMode) ? resolvedMode : "off";
}

export function resolveOptimizationLaunchPreviewMode(candidate, operationKind) {
  const candidates = [
    candidate,
    candidate?.tracking,
    candidate?.payload?.tracking,
    candidate?.payload,
  ];
  for (const item of candidates) {
    const field = readOptionalOptimizationField(item, "previewMode", "preview_mode");
    if (field.present) {
      return resolveOptionalOptimizationPreviewMode(item);
    }
  }

  // The launch response historically omitted this optional field. Keep the
  // reserve visible during that gap for Optimiser and Réinitialiser; status
  // polling still replaces it with the server's explicit off/boundary/solution mode.
  return operationKind === "optimization" || operationKind === "planning_reset"
    ? "solution"
    : "off";
}

function resolveOptionalOptimizationText(candidate, camelCaseKey, snakeCaseKey, fallback = null) {
  const field = readOptionalOptimizationField(candidate, camelCaseKey, snakeCaseKey);
  if (!field.present) {
    return fallback;
  }

  return typeof field.value === "string" && field.value.trim() ? field.value.trim() : null;
}

function buildDefaultOptimizationReferenceProbe() {
  return {
    confirmClickAt: null,
    confirmClickAtEpochMs: null,
    preflightMs: null,
    postOptimizeRoundtripMs: null,
    acceptedAt: null,
    acceptedAtEpochMs: null,
    acceptedToFirstPollMs: null,
    pollCount: 0,
    completedStatusSeenAt: null,
    completedStatusSeenAtEpochMs: null,
    completedToRevalidateMs: null,
    resultsLatestRoundtripMs: null,
    latestResponseRecordedAt: null,
    latestResponseRecordedAtEpochMs: null,
    latestToFirstPaintMs: null,
    latestRuntimeProfile: null,
    latestRequestProfile: null,
  };
}

function sanitizeOptimizationReferenceProbe(candidate) {
  const defaultProbe = buildDefaultOptimizationReferenceProbe();
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return defaultProbe;
  }

  return {
    confirmClickAt: typeof candidate.confirmClickAt === "string" && candidate.confirmClickAt.trim()
      ? candidate.confirmClickAt
      : null,
    confirmClickAtEpochMs: normalizeOptionalEpochMs(candidate.confirmClickAtEpochMs),
    preflightMs: normalizeOptionalDurationMs(candidate.preflightMs),
    postOptimizeRoundtripMs: normalizeOptionalDurationMs(candidate.postOptimizeRoundtripMs),
    acceptedAt: typeof candidate.acceptedAt === "string" && candidate.acceptedAt.trim()
      ? candidate.acceptedAt
      : null,
    acceptedAtEpochMs: normalizeOptionalEpochMs(candidate.acceptedAtEpochMs),
    acceptedToFirstPollMs: normalizeOptionalDurationMs(candidate.acceptedToFirstPollMs),
    pollCount: normalizeOptionalNonnegativeInt(candidate.pollCount) || 0,
    completedStatusSeenAt: typeof candidate.completedStatusSeenAt === "string" && candidate.completedStatusSeenAt.trim()
      ? candidate.completedStatusSeenAt
      : null,
    completedStatusSeenAtEpochMs: normalizeOptionalEpochMs(candidate.completedStatusSeenAtEpochMs),
    completedToRevalidateMs: normalizeOptionalDurationMs(candidate.completedToRevalidateMs),
    resultsLatestRoundtripMs: normalizeOptionalDurationMs(candidate.resultsLatestRoundtripMs),
    latestResponseRecordedAt: typeof candidate.latestResponseRecordedAt === "string" && candidate.latestResponseRecordedAt.trim()
      ? candidate.latestResponseRecordedAt
      : null,
    latestResponseRecordedAtEpochMs: normalizeOptionalEpochMs(candidate.latestResponseRecordedAtEpochMs),
    latestToFirstPaintMs: normalizeOptionalDurationMs(candidate.latestToFirstPaintMs),
    latestRuntimeProfile: sanitizeOptionalPlainObject(candidate.latestRuntimeProfile),
    latestRequestProfile: sanitizeOptionalPlainObject(candidate.latestRequestProfile),
  };
}

function mergeOptimizationReferenceProbe(currentProbe, patch) {
  const baseProbe = sanitizeOptimizationReferenceProbe(currentProbe);
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
    return baseProbe;
  }

  return sanitizeOptimizationReferenceProbe({
    ...baseProbe,
    ...patch,
    latestRuntimeProfile: Object.prototype.hasOwnProperty.call(patch, "latestRuntimeProfile")
      ? patch.latestRuntimeProfile
      : baseProbe.latestRuntimeProfile,
    latestRequestProfile: Object.prototype.hasOwnProperty.call(patch, "latestRequestProfile")
      ? patch.latestRequestProfile
      : baseProbe.latestRequestProfile,
  });
}

function buildDefaultOptimizationJobState() {
  return {
    jobId: null,
    dispatchDate: null,
    dispatchPeriod: null,
    operationKind: "optimization",
    status: "idle",
    message: "",
    error: null,
    errorDetails: null,
    detailItems: [],
    issueItems: [],
    resultUrl: getDefaultResultUrl(),
    pollUrl: null,
    recommendedPollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
    cancellationAllowed: false,
    captureContext: null,
    optimizationDiagnostics: null,
    progress: null,
    // Aperçu de calcul volontairement éphémère : ne jamais le sérialiser en sessionStorage.
    assignmentPreview: null,
    assignmentPreviewEvents: [],
    assignmentPreviewSnapshot: null,
    assignmentPreviewCursor: 0,
    assignmentPreviewLastReceivedRevision: 0,
    assignmentPreviewLatestRevision: 0,
    assignmentPreviewHasMore: false,
    checkpoint: null,
    previewMode: "off",
    completionReason: null,
    resultCommitted: false,
    referenceProbe: buildDefaultOptimizationReferenceProbe(),
    serviceHealth: [],
    startedAt: null,
    finishedAt: null,
    isRemote: false,
    isDismissed: false,
  };
}

export function sanitizeOptimizationJobState(candidate) {
  const defaultState = buildDefaultOptimizationJobState();
  if (!candidate || typeof candidate !== "object") {
    return defaultState;
  }

  const status = typeof candidate.status === "string"
    ? candidate.status.trim().toLowerCase()
    : defaultState.status;
  const safeStatus = ["idle", "running", "cancelling", "cancelled", "completed", "failed"].includes(status)
    ? status
    : defaultState.status;
  const pollUrl = typeof candidate.pollUrl === "string" && candidate.pollUrl.trim()
    ? candidate.pollUrl
    : (["running", "cancelling"].includes(safeStatus) ? DEFAULT_POLL_URL : null);
  const recommendedPollIntervalMs = Number.isFinite(candidate.recommendedPollIntervalMs)
    && candidate.recommendedPollIntervalMs > 0
    ? Math.round(candidate.recommendedPollIntervalMs)
    : DEFAULT_POLL_INTERVAL_MS;
  const jobId = typeof candidate.jobId === "string" && candidate.jobId.trim()
    ? candidate.jobId.trim()
    : (typeof candidate.job_id === "string" && candidate.job_id.trim() ? candidate.job_id.trim() : null);
  const dispatchDate = typeof candidate.dispatchDate === "string" && candidate.dispatchDate.trim()
    ? candidate.dispatchDate.trim()
    : (typeof candidate.dispatch_date === "string" && candidate.dispatch_date.trim()
      ? candidate.dispatch_date.trim()
      : null);
  const rawDispatchPeriod = typeof candidate.dispatchPeriod === "string"
    ? candidate.dispatchPeriod.trim().toLowerCase()
    : (typeof candidate.dispatch_period === "string" ? candidate.dispatch_period.trim().toLowerCase() : "");
  const dispatchPeriod = ["day", "evening"].includes(rawDispatchPeriod)
    ? rawDispatchPeriod
    : null;
  const operationKind = (candidate.operationKind || candidate.operation_kind) === "planning_reset"
    ? "planning_reset"
    : "optimization";
  const isRemote = candidate.isRemote === true;
  const cancellationAllowed = safeStatus === "running"
    && resolveOptionalOptimizationBoolean(
      candidate,
      "cancellationAllowed",
      "cancellation_allowed",
      !isRemote,
    );

  const assignmentPreviewEvents = sanitizeOptimizationPreviewEvents(
    candidate.assignmentPreviewEvents || candidate.assignment_preview_events,
  );
  const candidateAssignmentPreview = resolveOptionalOptimizationPreview(candidate);
  const assignmentPreviewSnapshot = resolveOptionalOptimizationPreview(
    candidate.assignmentPreviewSnapshot || candidate.assignment_preview_snapshot,
  ) || candidateAssignmentPreview;
  // The cursor is the last event revision actually received by this browser.
  // Keep it separate from the server's latest snapshot revision: a status page
  // can carry snapshot revision 20 while the event page still ends at 16.
  const explicitAssignmentPreviewLastReceivedRevision = normalizeOptionalOptimizationPreviewCursor(
    candidate.assignmentPreviewLastReceivedRevision
      ?? candidate.assignment_preview_last_received_revision
      ?? candidate.assignmentPreviewCursor
      ?? candidate.assignment_preview_cursor,
  );
  const latestEventRevision = assignmentPreviewEvents.length
    ? normalizeOptionalOptimizationPreviewCursor(
      assignmentPreviewEvents[assignmentPreviewEvents.length - 1]?.revision
        ?? assignmentPreviewEvents[assignmentPreviewEvents.length - 1]?.cursor,
    )
    : null;
  const assignmentPreviewLastReceivedRevision = explicitAssignmentPreviewLastReceivedRevision
    ?? latestEventRevision
    ?? 0;
  const hasEventStream = Object.prototype.hasOwnProperty.call(candidate, "assignment_preview_events")
    || Object.prototype.hasOwnProperty.call(candidate, "assignmentPreviewEvents");
  const latestEventPreview = assignmentPreviewEvents.at(-1)?.preview
    || assignmentPreviewEvents.at(-1)
    || null;
  const assignmentPreview = hasEventStream
    && explicitAssignmentPreviewLastReceivedRevision === null
    && latestEventPreview
    ? latestEventPreview
    : candidateAssignmentPreview;
  const assignmentPreviewLatestRevision = Math.max(
    Number(normalizeOptionalOptimizationPreviewCursor(
      candidate.assignmentPreviewLatestRevision
        ?? candidate.assignment_preview_latest_revision,
    )) || 0,
    Number(assignmentPreviewSnapshot?.revision || 0) || 0,
    ...assignmentPreviewEvents.map((event) => Number(event?.revision || event?.cursor || 0) || 0),
    Number(assignmentPreviewLastReceivedRevision) || 0,
  );

  return {
    jobId,
    dispatchDate,
    dispatchPeriod,
    operationKind,
    status: safeStatus,
    message: typeof candidate.message === "string" ? candidate.message : defaultState.message,
    error: typeof candidate.error === "string" && candidate.error.trim() ? candidate.error : null,
    errorDetails: safeStatus === "failed" ? sanitizeOptimizationJobErrorDetails(candidate) : null,
    detailItems: sanitizeOptimizationJobDetailItems(candidate.detailItems || candidate.detail_items),
    issueItems: sanitizeOptimizationJobIssueItems(candidate.issueItems || candidate.issue_items),
    resultUrl: typeof candidate.resultUrl === "string" && candidate.resultUrl.trim()
      ? appendDispatchDateToUrl(
        candidate.resultUrl,
        dispatchDate || undefined,
        dispatchPeriod || undefined,
      )
      : defaultState.resultUrl,
    pollUrl,
    recommendedPollIntervalMs,
    cancellationAllowed,
    captureContext: normalizeDispatchAlertCaptureContext(
      candidate.captureContext || candidate.capture_context,
    ),
    optimizationDiagnostics: sanitizeOptimizationJobDiagnostics(candidate),
    progress: sanitizeOptimizationProgressForStatus(candidate.progress, safeStatus),
    assignmentPreview,
    assignmentPreviewEvents,
    assignmentPreviewSnapshot,
    assignmentPreviewCursor: assignmentPreviewLastReceivedRevision,
    assignmentPreviewLastReceivedRevision,
    assignmentPreviewLatestRevision,
    assignmentPreviewHasMore: resolveOptimizationPreviewHasMore(candidate),
    checkpoint: resolveOptionalOptimizationCheckpoint(candidate),
    previewMode: resolveOptionalOptimizationPreviewMode(candidate),
    completionReason: resolveOptionalOptimizationText(candidate, "completionReason", "completion_reason"),
    resultCommitted: resolveOptionalOptimizationBoolean(candidate, "resultCommitted", "result_committed"),
    referenceProbe: sanitizeOptimizationReferenceProbe(candidate.referenceProbe || candidate.reference_probe),
    serviceHealth: sanitizeOptimizationJobServiceHealth(candidate.serviceHealth || candidate.service_health),
    startedAt: typeof candidate.startedAt === "string" && candidate.startedAt.trim() ? candidate.startedAt : null,
    finishedAt: typeof candidate.finishedAt === "string" && candidate.finishedAt.trim() ? candidate.finishedAt : null,
    isRemote,
    isDismissed: candidate.isDismissed === true,
  };
}

function readStoredOptimizationJobState() {
  if (typeof window === "undefined") {
    return buildDefaultOptimizationJobState();
  }

  try {
    const rawValue = window.sessionStorage.getItem(OPTIMIZATION_JOB_STORAGE_KEY);
    if (!rawValue) {
      return buildDefaultOptimizationJobState();
    }

    const storedState = sanitizeOptimizationJobState(JSON.parse(rawValue));
    // Une opération déjà terminée ne doit pas être annoncée à nouveau après F5.
    if (["completed", "failed", "cancelled"].includes(storedState.status)) {
      storedState.isDismissed = true;
    }
    return storedState;
  } catch {
    return buildDefaultOptimizationJobState();
  }
}

function persistOptimizationJobState(jobState) {
  if (typeof window === "undefined") {
    return;
  }

  const sanitizedState = sanitizeOptimizationJobState(jobState);
  if (sanitizedState.status === "idle") {
    window.sessionStorage.removeItem(OPTIMIZATION_JOB_STORAGE_KEY);
    return;
  }

  const {
    assignmentPreview: _assignmentPreview,
    assignmentPreviewEvents: _assignmentPreviewEvents,
    assignmentPreviewSnapshot: _assignmentPreviewSnapshot,
    ...persistedState
  } = sanitizedState;
  window.sessionStorage.setItem(OPTIMIZATION_JOB_STORAGE_KEY, JSON.stringify(persistedState));
}

async function parseJsonSafely(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function fetchOptimizationStatusPayload(pollUrl, signal) {
  const response = await fetchWithSupabaseAuth(pollUrl, {
    cache: "no-store",
    headers: {
      Accept: "application/json",
    },
    signal,
  });

  return {
    payload: await parseJsonSafely(response),
    response,
  };
}

function normalizeTrackedStatus(status, fallbackStatus = "running") {
  return typeof status === "string" && status.trim()
    ? status.trim().toLowerCase()
    : fallbackStatus;
}

export function buildOptimizationPreviewPollUrl(pollUrl, previewCursor = null) {
  const normalizedUrl = typeof pollUrl === "string" ? pollUrl.trim() : "";
  const normalizedCursor = normalizeOptionalOptimizationPreviewCursor(previewCursor);
  if (!normalizedUrl || normalizedCursor === null) {
    return normalizedUrl;
  }

  const hashIndex = normalizedUrl.indexOf("#");
  const hash = hashIndex >= 0 ? normalizedUrl.slice(hashIndex) : "";
  const urlWithoutHash = hashIndex >= 0 ? normalizedUrl.slice(0, hashIndex) : normalizedUrl;
  const queryParameter = `after_preview_revision=${encodeURIComponent(String(normalizedCursor))}`;
  if (/(?:\?|&)after_preview_revision=[^&]*/.test(urlWithoutHash)) {
    return `${urlWithoutHash.replace(
      /([?&])after_preview_revision=[^&]*/,
      `$1${queryParameter}`,
    )}${hash}`;
  }
  const separator = urlWithoutHash.includes("?") ? "&" : "?";
  return `${urlWithoutHash}${separator}${queryParameter}${hash}`;
}

function isValidOptimizationStatus(status) {
  return OPTIMIZATION_TRACKED_STATUSES.has(normalizeTrackedStatus(status, ""));
}

function resolveTrackedCancellationAllowed(payload, fallback = false, status = "running") {
  const normalizedStatus = normalizeTrackedStatus(status, "running");
  return normalizedStatus === "running"
    && resolveOptionalOptimizationBoolean(
      payload,
      "cancellationAllowed",
      "cancellation_allowed",
      fallback,
    );
}

export function resolveOptimizationPollingCancellationGate(currentState, payload, nextStatus) {
  const cancellationAllowed = resolveTrackedCancellationAllowed(
    payload,
    currentState?.cancellationAllowed,
    nextStatus,
  );
  const preserveCancellingState = currentState?.status === "cancelling"
    && nextStatus === "running";
  const preserveClosedGate = ["running", "cancelling"].includes(currentState?.status)
    && currentState?.cancellationAllowed === false
    && nextStatus === "running"
    && cancellationAllowed === true;

  return {
    cancellationAllowed: preserveClosedGate ? false : cancellationAllowed,
    preserveCancellingState,
    preserveClosedGate,
  };
}

export function shouldRetryOptimizationStatusPollFailure(
  failureCount,
  retryLimit = OPTIMIZATION_STATUS_POLL_RETRY_LIMIT,
) {
  const normalizedFailureCount = Number(failureCount);
  const normalizedRetryLimit = Number(retryLimit);
  if (!Number.isFinite(normalizedFailureCount) || normalizedFailureCount <= 0) {
    return false;
  }
  if (!Number.isFinite(normalizedRetryLimit) || normalizedRetryLimit < 0) {
    return false;
  }

  return normalizedFailureCount <= Math.round(normalizedRetryLimit);
}

export function isOptimizationStatusPayloadForJob(payload, jobId) {
  const payloadJobId = typeof payload?.job_id === "string" ? payload.job_id.trim() : "";
  const trackedJobId = typeof jobId === "string" ? jobId.trim() : "";
  return !payloadJobId || !trackedJobId || payloadJobId === trackedJobId;
}

export function applyOptimizationJobStateUpdateIfCurrent(currentState, trackedJobId, updateState) {
  if (typeof updateState !== "function") {
    return currentState;
  }
  const currentJobId = typeof currentState?.jobId === "string" ? currentState.jobId.trim() : "";
  const resolvedTrackedJobId = typeof trackedJobId === "string" ? trackedJobId.trim() : "";
  return currentJobId === resolvedTrackedJobId
    ? updateState(currentState)
    : currentState;
}

export function isOptimizationStatusPollAuthenticationFailure(status) {
  const normalizedStatus = Number(status);
  return normalizedStatus === 401 || normalizedStatus === 403;
}

// « idle » en 200 ou 404 : le serveur ne connaît plus ce calcul (redémarrage, redéploiement).
export function isOptimizationStatusPollUnknownJob(response, payload) {
  return (response?.ok === true || response?.status === 404)
    && normalizeTrackedStatus(payload?.status, "") === "idle";
}

function resolvePollFailureMessage(payload, fallbackMessage) {
  return typeof payload?.error === "string" && payload.error.trim()
    ? payload.error
    : (typeof payload?.message === "string" && payload.message.trim()
      ? payload.message
      : fallbackMessage);
}

function resolveTrackedResultUrl(payload, currentState) {
  const rawResultUrl = typeof payload?.result_url === "string" && payload.result_url.trim()
    ? payload.result_url
    : currentState.resultUrl || getDefaultResultUrl();
  const dispatchDate = typeof payload?.dispatch_date === "string" && payload.dispatch_date.trim()
    ? payload.dispatch_date.trim()
    : currentState.dispatchDate;
  const dispatchPeriod = typeof payload?.dispatch_period === "string" && payload.dispatch_period.trim()
    ? payload.dispatch_period.trim()
    : currentState.dispatchPeriod;

  return appendDispatchDateToUrl(
    rawResultUrl,
    dispatchDate || undefined,
    dispatchPeriod || undefined,
  );
}

function resolveTrackedMessage(payload, fallbackMessage) {
  return typeof payload?.message === "string" && payload.message.trim()
    ? payload.message
    : fallbackMessage;
}

function resolveTrackedServiceHealth(payload, fallbackServiceHealth) {
  if (Array.isArray(payload?.service_health) || Array.isArray(payload?.serviceHealth)) {
    return sanitizeOptimizationJobServiceHealth(payload?.service_health || payload?.serviceHealth);
  }

  return fallbackServiceHealth;
}

function resolveTrackedDetailItems(payload, fallbackDetailItems) {
  if (Array.isArray(payload?.detail_items) || Array.isArray(payload?.detailItems)) {
    return sanitizeOptimizationJobDetailItems(payload?.detail_items || payload?.detailItems);
  }

  return fallbackDetailItems;
}

function resolveTrackedIssueItems(payload, fallbackIssueItems) {
  if (Array.isArray(payload?.issue_items) || Array.isArray(payload?.issueItems)) {
    return sanitizeOptimizationJobIssueItems(payload?.issue_items || payload?.issueItems);
  }

  return fallbackIssueItems;
}

function resolveTrackedOptimizationDiagnostics(payload, fallbackDiagnostics) {
  const diagnostics = sanitizeOptimizationJobDiagnostics(payload);
  return hasTrackedOptimizationDiagnostics(payload) ? diagnostics : fallbackDiagnostics;
}

export function resolveTrackedOptimizationProgress(payload, fallbackProgress, jobStatus = "running") {
  const candidateProgress = sanitizeOptimizationProgressForStatus(payload?.progress, jobStatus);
  if (["completed", "failed", "cancelled"].includes(jobStatus)) {
    return candidateProgress
      ? mergeOptimizationProgress(fallbackProgress, candidateProgress)
      : null;
  }
  return mergeOptimizationProgress(fallbackProgress, candidateProgress);
}

function resolveTrackedOperationKind(payload, fallbackOperationKind = "optimization") {
  if (payload?.operation_kind === "planning_reset" || payload?.operationKind === "planning_reset") {
    return "planning_reset";
  }

  return fallbackOperationKind === "planning_reset" ? "planning_reset" : "optimization";
}

function buildRemoteOptimizationMessage(status, operationKind = "optimization") {
  if (operationKind === "planning_reset") {
    if (status === "cancelling") {
      return "Un autre utilisateur a demandé l'arrêt de la réinitialisation. Les modifications du planning restent temporairement bloquées.";
    }
    if (status === "completed") {
      return "La réinitialisation lancée par un autre utilisateur est terminée. Le planning a été actualisé.";
    }
    if (status === "cancelled") {
      return "La réinitialisation lancée par un autre utilisateur a été annulée. Le planning précédent reste disponible.";
    }
    if (status === "failed") {
      return "La réinitialisation lancée par un autre utilisateur a échoué. Le planning précédent reste disponible.";
    }
    return "Un autre utilisateur a lancé la réinitialisation du planning. Les modifications restent temporairement bloquées jusqu'à la fin du calcul.";
  }
  if (status === "cancelling") {
    return "Un autre utilisateur a demandé l'arrêt de l'optimisation. Les modifications du planning restent temporairement bloquées.";
  }
  if (status === "completed") {
    return "L'optimisation lancée par un autre utilisateur est terminée. Les résultats ont été actualisés.";
  }
  if (status === "cancelled") {
    return "L'optimisation lancée par un autre utilisateur a été annulée. Les résultats précédents restent affichés.";
  }
  if (status === "failed") {
    return "L'optimisation lancée par un autre utilisateur a échoué. Les résultats précédents restent affichés.";
  }
  return "Un autre utilisateur a lancé l'optimisation. Les modifications du planning sont temporairement bloquées jusqu'à la fin du calcul.";
}

export function buildDiscoveredOptimizationJobState(payload) {
  const status = normalizeTrackedStatus(payload?.status, "idle");
  const jobId = typeof payload?.job_id === "string" ? payload.job_id.trim() : "";
  const operationKind = resolveTrackedOperationKind(payload);
  if (!jobId || !["running", "cancelling"].includes(status)) {
    return null;
  }

  const discoveredPreview = resolveOptionalOptimizationPreview(payload);
  const discoveredBaselinePreview = discoveredPreview || null;
  const discoveredBaselineRevision = Number(discoveredBaselinePreview?.revision || 0);
  const discoveredLatestRevision = normalizeOptionalOptimizationPreviewCursor(
    resolveOptimizationPreviewLatestRevision(payload, discoveredBaselineRevision),
  ) ?? discoveredBaselineRevision;
  // Passive discovery/reload starts from the server's current portrait. It
  // must not replay the historical event buffer; active jobs observed from a
  // launch use the separate last-received cursor and start at zero.
  const previewState = {
    assignmentPreview: discoveredBaselinePreview,
    assignmentPreviewEvents: [],
    assignmentPreviewSnapshot: discoveredPreview || discoveredBaselinePreview,
    assignmentPreviewCursor: discoveredBaselineRevision,
    assignmentPreviewLastReceivedRevision: discoveredBaselineRevision,
    assignmentPreviewLatestRevision: Math.max(discoveredBaselineRevision, Number(discoveredLatestRevision) || 0),
    assignmentPreviewHasMore: false,
  };
  return sanitizeOptimizationJobState({
    jobId,
    dispatchDate: payload?.dispatch_date,
    dispatchPeriod: payload?.dispatch_period,
    operationKind,
    status,
    message: buildRemoteOptimizationMessage(status, operationKind),
    error: null,
    resultUrl: payload?.result_url,
    pollUrl: DEFAULT_POLL_URL,
    recommendedPollIntervalMs: payload?.recommended_poll_interval_ms,
    cancellationAllowed: resolveTrackedCancellationAllowed(payload, false, status),
    optimizationDiagnostics: sanitizeOptimizationJobDiagnostics(payload),
    progress: sanitizeOptimizationProgress(payload?.progress),
    ...previewState,
    checkpoint: resolveOptionalOptimizationCheckpoint(payload),
    previewMode: resolveOptionalOptimizationPreviewMode(payload),
    completionReason: resolveOptionalOptimizationText(payload, "completionReason", "completion_reason"),
    resultCommitted: resolveOptionalOptimizationBoolean(payload, "resultCommitted", "result_committed"),
    serviceHealth: payload?.service_health,
    startedAt: payload?.started_at,
    finishedAt: payload?.finished_at,
    isRemote: true,
    isDismissed: false,
  });
}

export function canRequestOptimizationCancellation(jobState) {
  return jobState?.isRemote !== true
    && jobState?.status === "running"
    && jobState?.cancellationAllowed === true
    && typeof jobState?.jobId === "string"
    && Boolean(jobState.jobId.trim());
}

function buildOptimizationCompletionInvalidationUrls(resultUrl = "", operationKind = "optimization") {
  return [
    ...OPTIMIZATION_COMPLETION_INVALIDATED_API_URLS,
    ...(operationKind === "planning_reset" ? PLANNING_RESET_COMPLETION_INVALIDATED_API_URLS : []),
    ...buildResultsLatestApiInvalidationUrls({
      currentSearch: typeof window === "undefined" ? "" : (window.location.search || ""),
      resultUrl,
    }),
  ];
}

function invalidateCommittedOptimizationResults(resultUrl, operationKind) {
  invalidatePageDataCacheEntries(
    buildOptimizationCompletionInvalidationUrls(resultUrl, operationKind),
  );
  invalidateShellDataCache();
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function" && typeof CustomEvent === "function") {
    window.dispatchEvent(new CustomEvent("grain:optimization-results-committed", {
      detail: { operationKind, resultUrl },
    }));
  }
}

function isOptimizationPreviewReadyForCommittedResults(jobState) {
  if (jobState?.assignmentPreviewHasMore === true) {
    return false;
  }

  const lastReceivedRevision = Number(jobState?.assignmentPreviewLastReceivedRevision
    ?? jobState?.assignmentPreviewCursor
    ?? 0);
  const latestRevision = Number(jobState?.assignmentPreviewLatestRevision || 0);
  if (Number.isFinite(latestRevision)
    && latestRevision > 0
    && Number.isFinite(lastReceivedRevision)
    && lastReceivedRevision < latestRevision) {
    return false;
  }

  const previews = [jobState?.assignmentPreview, jobState?.assignmentPreviewSnapshot]
    .filter((preview) => preview && typeof preview === "object");
  if (!previews.length) {
    return true;
  }

  // Prefer the explicit final boundary when the v2 stream provides it, while
  // accepting a fully drained non-canonical/legacy snapshot for compatibility.
  const hasCanonicalBoundary = previews.some((preview) => (
    Number(preview?.revision || 0) > 0
      && String(preview?.stageKey || preview?.stage_key || "").trim().toLowerCase() === "final_verification"
      && String(preview?.source || "").trim().toLowerCase() === "final_boundary"
  ));
  return hasCanonicalBoundary || jobState?.previewMode === "off" || latestRevision <= lastReceivedRevision;
}

export function buildOptimizationCancellationState(currentState, payload, nextStatus, { isConflict = false } = {}) {
  const normalizedStatus = normalizeTrackedStatus(nextStatus, "cancelling");
  const resolvedStatus = isConflict && normalizedStatus === "cancelling"
    ? "running"
    : normalizedStatus;
  const operationKind = resolveTrackedOperationKind(payload, currentState.operationKind);
  const fallbackMessage = isConflict
    ? "Sauvegarde finale déjà commencée; l’arrêt n’est plus disponible."
    : resolvedStatus === "cancelled"
      ? operationKind === "planning_reset"
        ? "Réinitialisation du planning annulée."
        : "Optimisation annulée."
      : "Annulation demandée.";
  const previewState = resolveTrackedOptimizationPreviewState(payload, currentState);

  return sanitizeOptimizationJobState({
    ...currentState,
    jobId: typeof payload?.job_id === "string" && payload.job_id.trim()
      ? payload.job_id.trim()
      : currentState.jobId,
    dispatchDate: typeof payload?.dispatch_date === "string" && payload.dispatch_date.trim()
      ? payload.dispatch_date.trim()
      : currentState.dispatchDate,
    dispatchPeriod: typeof payload?.dispatch_period === "string" && payload.dispatch_period.trim()
      ? payload.dispatch_period.trim()
      : currentState.dispatchPeriod,
    operationKind,
    status: resolvedStatus,
    message: resolveTrackedMessage(payload, fallbackMessage),
    error: resolvedStatus === "failed"
      ? resolveTrackedMessage(
        payload,
        operationKind === "planning_reset"
          ? "Impossible d'arrêter la réinitialisation en cours."
          : "Impossible d'arrêter l'optimisation en cours.",
      )
      : null,
    detailItems: resolvedStatus === "failed"
      ? resolveTrackedDetailItems(payload, currentState.detailItems)
      : [],
    errorDetails: resolvedStatus === "failed" ? sanitizeOptimizationJobErrorDetails(payload) : null,
    issueItems: resolvedStatus === "failed"
      ? resolveTrackedIssueItems(payload, currentState.issueItems)
      : [],
    resultUrl: resolveTrackedResultUrl(payload, currentState),
    // A successful click must close the cancellation gate immediately, even
    // when the server still reports the job as running for one poll cycle.
    cancellationAllowed: false,
    optimizationDiagnostics: resolveTrackedOptimizationDiagnostics(payload, currentState.optimizationDiagnostics),
    progress: resolveTrackedOptimizationProgress(payload, currentState.progress, resolvedStatus),
    ...previewState,
    checkpoint: resolveOptionalOptimizationCheckpoint(payload, currentState.checkpoint),
    previewMode: resolveOptionalOptimizationPreviewMode(payload, currentState.previewMode),
    completionReason: resolveOptionalOptimizationText(payload, "completionReason", "completion_reason", currentState.completionReason),
    resultCommitted: resolveOptionalOptimizationBoolean(payload, "resultCommitted", "result_committed", currentState.resultCommitted),
    serviceHealth: resolveTrackedServiceHealth(payload, currentState.serviceHealth),
    finishedAt: ["completed", "failed", "cancelled"].includes(resolvedStatus)
      ? (typeof payload?.finished_at === "string" && payload.finished_at.trim()
        ? payload.finished_at
        : (currentState.finishedAt || new Date().toISOString()))
      : null,
    isDismissed: false,
  });
}

export function OptimizationJobProvider({
  children,
  isAccessAuthError = false,
  isAccessReady = false,
  isPollingAllowed = true,
  session = null,
}) {
  const [jobState, setJobState] = React.useState(readStoredOptimizationJobState);
  // Covers the request before a server job exists; never persisted as a job.
  const [planningResetLaunchScope, setPlanningResetLaunchScope] = React.useState(null);
  const assignmentPreviewCursorByJobRef = React.useRef(new Map());
  const committedOptimizationJobRef = React.useRef("");
  const [optimizationPollingEnabled, setOptimizationPollingEnabled] = React.useState(
    () => typeof window === "undefined"
      || isOptimizationPollingEnabledForPathname(window.location.pathname),
  );
  const [optimizationPollingAuthBlocked, setOptimizationPollingAuthBlocked] = React.useState(false);
  const sessionUserId = typeof session?.user?.id === "string" ? session.user.id.trim() : "";
  const sessionAccessToken = typeof session?.access_token === "string" ? session.access_token : "";
  const isOptimizationPollingAuthenticated = isAccessReady
    && !isAccessAuthError
    && isPollingAllowed
    && Boolean(sessionUserId)
    && !optimizationPollingAuthBlocked;

  React.useEffect(() => {
    setOptimizationPollingAuthBlocked(false);
  }, [isAccessAuthError, sessionAccessToken, sessionUserId]);

  React.useEffect(() => {
    persistOptimizationJobState(jobState);
  }, [jobState]);

  React.useEffect(() => {
    const trackedJobId = typeof jobState?.jobId === "string" ? jobState.jobId.trim() : "";
    const cursor = normalizeOptionalOptimizationPreviewCursor(jobState?.assignmentPreviewCursor);
    if (trackedJobId && cursor !== null) {
      assignmentPreviewCursorByJobRef.current.set(trackedJobId, cursor);
    }
  }, [jobState?.assignmentPreviewCursor, jobState?.jobId]);

  React.useEffect(() => {
    const trackedJobId = typeof jobState?.jobId === "string" ? jobState.jobId.trim() : "";
    const didCommitResults = jobState?.resultCommitted === true
      && (jobState?.status === "completed" || jobState?.status === "cancelled");
    if (!trackedJobId
      || !didCommitResults
      || !isOptimizationPreviewReadyForCommittedResults(jobState)
      || committedOptimizationJobRef.current === trackedJobId) {
      return;
    }

    committedOptimizationJobRef.current = trackedJobId;
    invalidateCommittedOptimizationResults(jobState.resultUrl, jobState.operationKind);
  }, [
    jobState?.jobId,
    jobState?.operationKind,
    jobState?.resultCommitted,
    jobState?.resultUrl,
    jobState?.status,
    jobState?.assignmentPreviewHasMore,
    jobState?.assignmentPreviewLastReceivedRevision,
    jobState?.assignmentPreviewLatestRevision,
    jobState?.assignmentPreview,
    jobState?.assignmentPreviewSnapshot,
    jobState?.previewMode,
  ]);

  React.useEffect(() => {
    if (
      !optimizationPollingEnabled
      || !isOptimizationPollingAuthenticated
      || ["running", "cancelling"].includes(jobState.status)
      || typeof window === "undefined"
    ) {
      return undefined;
    }

    let isDisposed = false;
    let timeoutId = null;
    let abortController = null;
    const discoverySourceJobId = jobState.jobId;

    const scheduleNextDiscovery = () => {
      timeoutId = window.setTimeout(() => {
        void discoverActiveOptimization();
      }, DEFAULT_POLL_INTERVAL_MS);
    };

    const discoverActiveOptimization = async () => {
      abortController = new AbortController();
      try {
        // La découverte ne garde qu'un calcul en cours : le serveur omet le détail d'un calcul terminé.
        const { payload, response } = await fetchOptimizationStatusPayload(
          `${DEFAULT_POLL_URL}?discovery=1`,
          abortController.signal,
        );
        if (isDisposed) {
          return;
        }

        if (isOptimizationStatusPollAuthenticationFailure(response.status)) {
          setOptimizationPollingAuthBlocked(true);
          return;
        }

        const discoveredState = response.ok
          ? buildDiscoveredOptimizationJobState(payload)
          : null;
        if (discoveredState) {
          setJobState((currentState) => applyOptimizationJobStateUpdateIfCurrent(
            currentState,
            discoverySourceJobId,
            () => discoveredState,
          ));
          return;
        }
      } catch (error) {
        if (isDisposed || error?.name === "AbortError") {
          return;
        }
      }

      scheduleNextDiscovery();
    };

    void discoverActiveOptimization();

    return () => {
      isDisposed = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      abortController?.abort();
    };
  }, [
    isOptimizationPollingAuthenticated,
    jobState.jobId,
    jobState.status,
    optimizationPollingEnabled,
    sessionAccessToken,
    sessionUserId,
  ]);

  React.useEffect(() => {
    if (
      !optimizationPollingEnabled
      || !isOptimizationPollingAuthenticated
      || (!["running", "cancelling"].includes(jobState.status)
        && jobState.assignmentPreviewHasMore !== true)
      || !jobState.pollUrl
      || typeof window === "undefined"
    ) {
      return undefined;
    }

    let isDisposed = false;
    let timeoutId = null;
    let abortController = null;
    let consecutivePollFailureCount = 0;
    const trackedJobId = jobState.jobId;
    const setTrackedJobState = (updateState) => {
      setJobState((currentState) => applyOptimizationJobStateUpdateIfCurrent(
        currentState,
        trackedJobId,
        updateState,
      ));
    };

    const scheduleNextPoll = (status = jobState.status, { immediate = false } = {}) => {
      const pollIntervalMs = status === "cancelling"
        ? OPTIMIZATION_CANCELLING_POLL_INTERVAL_MS
        : immediate
          ? 0
          : (jobState.recommendedPollIntervalMs || DEFAULT_POLL_INTERVAL_MS);
      timeoutId = window.setTimeout(() => {
        void pollOptimizationStatus();
      }, pollIntervalMs);
    };

    const pollOptimizationStatus = async () => {
      abortController = new AbortController();
      const pollStartedAtEpochMs = Date.now();

      setTrackedJobState((currentState) => ({
        ...currentState,
        referenceProbe: mergeOptimizationReferenceProbe(currentState.referenceProbe, {
          acceptedToFirstPollMs: currentState.referenceProbe?.acceptedToFirstPollMs == null
            && normalizeOptionalEpochMs(currentState.referenceProbe?.acceptedAtEpochMs) != null
            ? normalizeOptionalDurationMs(pollStartedAtEpochMs - Number(currentState.referenceProbe.acceptedAtEpochMs))
            : currentState.referenceProbe?.acceptedToFirstPollMs,
          pollCount: (normalizeOptionalNonnegativeInt(currentState.referenceProbe?.pollCount) || 0) + 1,
        }),
      }));

      try {
        const knownPreviewCursor = assignmentPreviewCursorByJobRef.current.get(trackedJobId)
          ?? jobState.assignmentPreviewLastReceivedRevision
          ?? jobState.assignmentPreviewCursor
          ?? 0;
        let pollResult = await fetchOptimizationStatusPayload(
          buildOptimizationPreviewPollUrl(jobState.pollUrl, knownPreviewCursor),
          abortController.signal,
        );
        let { payload, response } = pollResult;

        if (isDisposed) {
          return;
        }
        if (isOptimizationStatusPollAuthenticationFailure(response.status)) {
          setOptimizationPollingAuthBlocked(true);
          return;
        }

        if (!response.ok && jobState.pollUrl !== DEFAULT_POLL_URL) {
          try {
            const fallbackPollResult = await fetchOptimizationStatusPayload(
              buildOptimizationPreviewPollUrl(DEFAULT_POLL_URL, knownPreviewCursor),
              abortController.signal,
            );
            if (isDisposed) {
              return;
            }
            if (isOptimizationStatusPollAuthenticationFailure(fallbackPollResult.response.status)) {
              setOptimizationPollingAuthBlocked(true);
              return;
            }
            const fallbackStatus = normalizeTrackedStatus(fallbackPollResult.payload?.status, "idle");
            if (fallbackPollResult.response.ok && fallbackStatus !== "idle") {
              pollResult = fallbackPollResult;
              payload = pollResult.payload;
              response = pollResult.response;
            }
          } catch {}
        }

        if (isDisposed) {
          return;
        }

        if (isOptimizationStatusPollAuthenticationFailure(response.status)) {
          setOptimizationPollingAuthBlocked(true);
          return;
        }

        // Calcul d'une autre session perdu par le serveur : fin du suivi, résultat relu, clé effacée.
        if (jobState.isRemote && isOptimizationStatusPollUnknownJob(response, payload)) {
          invalidateCommittedOptimizationResults(jobState.resultUrl, jobState.operationKind);
          setTrackedJobState(() => buildDefaultOptimizationJobState());
          return;
        }

        if (!response.ok) {
          consecutivePollFailureCount += 1;
          if (shouldRetryOptimizationStatusPollFailure(consecutivePollFailureCount)) {
            scheduleNextPoll();
            return;
          }

          const errorMessage = resolvePollFailureMessage(
            payload,
            jobState.operationKind === "planning_reset"
              ? "Le suivi de la réinitialisation n'est plus disponible."
              : "Le suivi de l'optimisation n'est plus disponible.",
          );

          setTrackedJobState((currentState) => ({
            ...currentState,
            status: "failed",
            message: errorMessage,
            error: errorMessage,
            detailItems: [],
            issueItems: [],
            progress: null,
            cancellationAllowed: false,
            finishedAt: currentState.finishedAt || new Date().toISOString(),
            isDismissed: false,
          }));
          return;
        }

        consecutivePollFailureCount = 0;

        const nextStatus = normalizeTrackedStatus(payload?.status, "running");
        const nextOperationKind = resolveTrackedOperationKind(payload, jobState.operationKind);
        if (!isOptimizationStatusPayloadForJob(payload, jobState.jobId)) {
          const replacementState = buildDiscoveredOptimizationJobState(payload);
          setTrackedJobState(() => replacementState || buildDefaultOptimizationJobState());
          return;
        }

        // The effect closure can be older than the state committed by the
        // previous poll. Use the per-job cursor as the only poll-loop base for
        // advancing the ref; each React updater below resolves against its
        // actual current state before committing the preview metadata.
        const previewCursorState = resolveTrackedOptimizationPreviewState(payload, {
          ...jobState,
          assignmentPreviewCursor: knownPreviewCursor,
          assignmentPreviewLastReceivedRevision: knownPreviewCursor,
        });
        if (trackedJobId && previewCursorState.assignmentPreviewLastReceivedRevision != null) {
          assignmentPreviewCursorByJobRef.current.set(
            trackedJobId,
            previewCursorState.assignmentPreviewLastReceivedRevision,
          );
        }
        const payloadHasPreviewMore = resolveOptimizationPreviewHasMore(payload, false);

        if (nextStatus === "running" || nextStatus === "cancelling") {
          setTrackedJobState((currentState) => {
            const cancellationGate = resolveOptimizationPollingCancellationGate(
              currentState,
              payload,
              nextStatus,
            );
            if (cancellationGate.preserveCancellingState) {
              return currentState;
            }
            return {
              ...currentState,
              jobId: typeof payload?.job_id === "string" && payload.job_id.trim()
                ? payload.job_id.trim()
                : currentState.jobId,
              dispatchDate: typeof payload?.dispatch_date === "string" && payload.dispatch_date.trim()
                ? payload.dispatch_date.trim()
                : currentState.dispatchDate,
              dispatchPeriod: typeof payload?.dispatch_period === "string" && payload.dispatch_period.trim()
                ? payload.dispatch_period.trim()
                : currentState.dispatchPeriod,
              operationKind: nextOperationKind,
              status: nextStatus,
              message: cancellationGate.preserveClosedGate
                ? currentState.message
                : currentState.isRemote
                  ? buildRemoteOptimizationMessage(nextStatus, nextOperationKind)
                  : resolveTrackedMessage(
                    payload,
                    nextStatus === "cancelling"
                      ? "Annulation demandée."
                      : (currentState.message || (nextOperationKind === "planning_reset"
                        ? "Réinitialisation en cours."
                        : "Optimisation en cours.")),
                  ),
              error: null,
              detailItems: [],
              issueItems: [],
              cancellationAllowed: cancellationGate.cancellationAllowed,
              optimizationDiagnostics: resolveTrackedOptimizationDiagnostics(payload, currentState.optimizationDiagnostics),
              progress: resolveTrackedOptimizationProgress(payload, currentState.progress, nextStatus),
              ...resolveTrackedOptimizationPreviewState(payload, currentState),
              checkpoint: resolveOptionalOptimizationCheckpoint(payload, currentState.checkpoint),
              previewMode: resolveOptionalOptimizationPreviewMode(payload, currentState.previewMode),
              completionReason: resolveOptionalOptimizationText(payload, "completionReason", "completion_reason", currentState.completionReason),
              resultCommitted: resolveOptionalOptimizationBoolean(payload, "resultCommitted", "result_committed", currentState.resultCommitted),
              serviceHealth: resolveTrackedServiceHealth(payload, currentState.serviceHealth),
              startedAt: typeof payload?.started_at === "string" && payload.started_at.trim()
                ? payload.started_at
                : currentState.startedAt,
              isDismissed: false,
            };
          });
          scheduleNextPoll(nextStatus, {
            immediate: payloadHasPreviewMore,
          });
          return;
        }

        if (nextStatus === "completed") {
          const resolvedResultUrl = resolveTrackedResultUrl(payload, jobState);
          const completedStatusSeenAtEpochMs = Date.now();

          setTrackedJobState((currentState) => ({
            ...currentState,
            jobId: typeof payload?.job_id === "string" && payload.job_id.trim()
              ? payload.job_id.trim()
              : currentState.jobId,
            operationKind: nextOperationKind,
            status: "completed",
            message: currentState.isRemote
              ? buildRemoteOptimizationMessage("completed", nextOperationKind)
              : resolveTrackedMessage(
                payload,
                nextOperationKind === "planning_reset"
                  ? "Réinitialisation du planning terminée."
                  : "Mise à jour terminée.",
              ),
            error: null,
            detailItems: [],
            issueItems: [],
            resultUrl: resolvedResultUrl,
            cancellationAllowed: false,
            optimizationDiagnostics: resolveTrackedOptimizationDiagnostics(payload, currentState.optimizationDiagnostics),
            progress: resolveTrackedOptimizationProgress(payload, currentState.progress, nextStatus),
            ...resolveTrackedOptimizationPreviewState(payload, currentState),
            checkpoint: resolveOptionalOptimizationCheckpoint(payload, currentState.checkpoint),
            previewMode: resolveOptionalOptimizationPreviewMode(payload, currentState.previewMode),
            completionReason: resolveOptionalOptimizationText(payload, "completionReason", "completion_reason", currentState.completionReason),
            resultCommitted: resolveOptionalOptimizationBoolean(payload, "resultCommitted", "result_committed", currentState.resultCommitted),
            referenceProbe: mergeOptimizationReferenceProbe(
              currentState.referenceProbe,
              currentState.referenceProbe?.completedStatusSeenAtEpochMs == null
                ? {
                  completedStatusSeenAt: new Date(completedStatusSeenAtEpochMs).toISOString(),
                  completedStatusSeenAtEpochMs,
                }
                : null,
            ),
            serviceHealth: resolveTrackedServiceHealth(payload, currentState.serviceHealth),
            finishedAt: typeof payload?.finished_at === "string" && payload.finished_at.trim()
              ? payload.finished_at
              : (currentState.finishedAt || new Date().toISOString()),
            isDismissed: currentState.status === nextStatus && currentState.isDismissed,
          }));
          if (payloadHasPreviewMore) {
            scheduleNextPoll(nextStatus, { immediate: true });
          }
          return;
        }

        if (nextStatus === "failed") {
          const errorMessage = typeof payload?.error === "string" && payload.error.trim()
            ? payload.error
            : resolveTrackedMessage(
              payload,
              nextOperationKind === "planning_reset"
                ? "La réinitialisation du planning a échoué."
                : "L'optimisation a échoué.",
            );

          setTrackedJobState((currentState) => ({
            ...currentState,
            jobId: typeof payload?.job_id === "string" && payload.job_id.trim()
              ? payload.job_id.trim()
              : currentState.jobId,
            operationKind: nextOperationKind,
            status: "failed",
            message: errorMessage,
            error: errorMessage,
            errorDetails: sanitizeOptimizationJobErrorDetails(payload),
            detailItems: resolveTrackedDetailItems(payload, currentState.detailItems),
            issueItems: resolveTrackedIssueItems(payload, currentState.issueItems),
            cancellationAllowed: false,
            optimizationDiagnostics: resolveTrackedOptimizationDiagnostics(payload, currentState.optimizationDiagnostics),
            progress: resolveTrackedOptimizationProgress(payload, currentState.progress, nextStatus),
            ...resolveTrackedOptimizationPreviewState(payload, currentState),
            checkpoint: resolveOptionalOptimizationCheckpoint(payload, currentState.checkpoint),
            previewMode: resolveOptionalOptimizationPreviewMode(payload, currentState.previewMode),
            completionReason: resolveOptionalOptimizationText(payload, "completionReason", "completion_reason", currentState.completionReason),
            resultCommitted: resolveOptionalOptimizationBoolean(payload, "resultCommitted", "result_committed", currentState.resultCommitted),
            serviceHealth: resolveTrackedServiceHealth(payload, currentState.serviceHealth),
            finishedAt: typeof payload?.finished_at === "string" && payload.finished_at.trim()
              ? payload.finished_at
              : (currentState.finishedAt || new Date().toISOString()),
            isDismissed: currentState.status === nextStatus && currentState.isDismissed,
          }));
          return;
        }

        if (nextStatus === "cancelled") {
          const resolvedResultUrl = resolveTrackedResultUrl(payload, jobState);

          setTrackedJobState((currentState) => ({
            ...currentState,
            jobId: typeof payload?.job_id === "string" && payload.job_id.trim()
              ? payload.job_id.trim()
              : currentState.jobId,
            operationKind: nextOperationKind,
            status: "cancelled",
            message: currentState.isRemote
              ? buildRemoteOptimizationMessage("cancelled", nextOperationKind)
              : resolveTrackedMessage(
                payload,
                nextOperationKind === "planning_reset"
                  ? "Réinitialisation du planning annulée."
                  : "Optimisation annulée.",
              ),
            error: null,
            detailItems: [],
            issueItems: [],
            resultUrl: resolvedResultUrl,
            cancellationAllowed: false,
            optimizationDiagnostics: resolveTrackedOptimizationDiagnostics(payload, currentState.optimizationDiagnostics),
            progress: resolveTrackedOptimizationProgress(payload, currentState.progress, nextStatus),
            ...resolveTrackedOptimizationPreviewState(payload, currentState),
            checkpoint: resolveOptionalOptimizationCheckpoint(payload, currentState.checkpoint),
            previewMode: resolveOptionalOptimizationPreviewMode(payload, currentState.previewMode),
            completionReason: resolveOptionalOptimizationText(payload, "completionReason", "completion_reason", currentState.completionReason),
            resultCommitted: resolveOptionalOptimizationBoolean(payload, "resultCommitted", "result_committed", currentState.resultCommitted),
            serviceHealth: resolveTrackedServiceHealth(payload, currentState.serviceHealth),
            finishedAt: typeof payload?.finished_at === "string" && payload.finished_at.trim()
              ? payload.finished_at
              : (currentState.finishedAt || new Date().toISOString()),
            isDismissed: currentState.status === nextStatus && currentState.isDismissed,
          }));
          return;
        }

        scheduleNextPoll();
      } catch (error) {
        if (isDisposed || error?.name === "AbortError") {
          return;
        }

        consecutivePollFailureCount += 1;
        if (shouldRetryOptimizationStatusPollFailure(consecutivePollFailureCount)) {
          scheduleNextPoll();
          return;
        }

        const errorMessage = error instanceof Error && error.message
          ? error.message
          : jobState.operationKind === "planning_reset"
            ? "Le suivi de la réinitialisation a été interrompu."
            : "Le suivi de l'optimisation a été interrompu.";

        setTrackedJobState((currentState) => ({
          ...currentState,
          status: "failed",
          message: errorMessage,
          error: errorMessage,
          detailItems: [],
          issueItems: [],
          progress: null,
          cancellationAllowed: false,
          serviceHealth: currentState.serviceHealth,
          finishedAt: currentState.finishedAt || new Date().toISOString(),
          isDismissed: false,
        }));
      }
    };

    void pollOptimizationStatus();

    return () => {
      isDisposed = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      if (abortController) {
        abortController.abort();
      }
    };
  }, [
    isOptimizationPollingAuthenticated,
    jobState.pollUrl,
    jobState.jobId,
    jobState.recommendedPollIntervalMs,
    jobState.operationKind,
    jobState.status,
    jobState.assignmentPreviewHasMore,
    optimizationPollingEnabled,
    sessionAccessToken,
    sessionUserId,
  ]);

  const trackOptimizationLaunch = React.useCallback((launchState) => {
    const launchStatus = typeof launchState?.status === "string"
      ? launchState.status.trim().toLowerCase()
      : "running";
    const resolvedResultUrl = typeof launchState?.resultUrl === "string" && launchState.resultUrl.trim()
      ? appendDispatchDateToUrl(launchState.resultUrl, launchState.dispatchDate || launchState.dispatch_date || undefined, launchState.dispatchPeriod || launchState.dispatch_period || undefined)
      : getDefaultResultUrl();
    const operationKind = resolveTrackedOperationKind(launchState);
    const launchJobId = typeof (launchState?.jobId || launchState?.job_id) === "string"
      ? (launchState.jobId || launchState.job_id).trim()
      : "";
    if (launchJobId) {
      assignmentPreviewCursorByJobRef.current.set(launchJobId, 0);
    }

    const nextState = sanitizeOptimizationJobState({
      jobId: launchState?.jobId || launchState?.job_id,
      dispatchDate: launchState?.dispatchDate || launchState?.dispatch_date,
      dispatchPeriod: launchState?.dispatchPeriod || launchState?.dispatch_period,
      operationKind,
      status: launchStatus === "completed"
        ? "completed"
        : launchStatus === "failed"
          ? "failed"
          : launchStatus === "cancelled"
            ? "cancelled"
          : "running",
      message: typeof launchState?.message === "string" && launchState.message.trim()
        ? launchState.message
        : launchStatus === "failed"
          ? operationKind === "planning_reset"
            ? "La réinitialisation du planning a échoué."
            : "L'optimisation a échoué."
          : launchStatus === "cancelled"
            ? operationKind === "planning_reset"
              ? "Réinitialisation du planning annulée."
              : "Optimisation annulée."
          : operationKind === "planning_reset"
            ? "Réinitialisation en cours."
            : "Optimisation en cours.",
      error: typeof launchState?.error === "string" && launchState.error.trim()
        ? launchState.error
        : null,
      detailItems: sanitizeOptimizationJobDetailItems(launchState?.detailItems || launchState?.detail_items),
      errorDetails: sanitizeOptimizationJobErrorDetails(launchState),
      issueItems: sanitizeOptimizationJobIssueItems(launchState?.issueItems || launchState?.issue_items),
      resultUrl: resolvedResultUrl,
      pollUrl: typeof launchState?.pollUrl === "string" && launchState.pollUrl.trim()
        ? launchState.pollUrl
        : DEFAULT_POLL_URL,
      recommendedPollIntervalMs: Number.isFinite(launchState?.recommendedPollIntervalMs)
        ? launchState.recommendedPollIntervalMs
        : DEFAULT_POLL_INTERVAL_MS,
      cancellationAllowed: resolveOptionalOptimizationBoolean(
        launchState,
        "cancellationAllowed",
        "cancellation_allowed",
        launchState?.alreadyRunning !== true
          && (launchStatus === "accepted" || launchStatus === "running"),
      ),
      captureContext: launchState?.captureContext || launchState?.capture_context || null,
      optimizationDiagnostics: sanitizeOptimizationJobDiagnostics(launchState),
      progress: sanitizeOptimizationProgress(launchState?.progress),
      ...resolveTrackedOptimizationPreviewState(launchState, {
        assignmentPreview: null,
        assignmentPreviewSnapshot: null,
        assignmentPreviewCursor: 0,
        assignmentPreviewLastReceivedRevision: 0,
        assignmentPreviewHasMore: false,
      }),
      checkpoint: resolveOptionalOptimizationCheckpoint(launchState),
      previewMode: resolveOptimizationLaunchPreviewMode(launchState, operationKind),
      completionReason: resolveOptionalOptimizationText(launchState, "completionReason", "completion_reason"),
      resultCommitted: resolveOptionalOptimizationBoolean(launchState, "resultCommitted", "result_committed"),
      referenceProbe: sanitizeOptimizationReferenceProbe(launchState?.referenceProbe),
      serviceHealth: sanitizeOptimizationJobServiceHealth(launchState?.serviceHealth || launchState?.service_health),
      startedAt: launchState?.startedAt || new Date().toISOString(),
      finishedAt: launchStatus === "completed" || launchStatus === "failed" || launchStatus === "cancelled"
        ? (launchState?.finishedAt || new Date().toISOString())
        : null,
      isRemote: launchState?.alreadyRunning === true,
      isDismissed: false,
    });

    setJobState(nextState);
    return nextState;
  }, []);

  const requestOptimizationCancel = React.useCallback(async () => {
    if (!canRequestOptimizationCancellation(jobState)) {
      return jobState;
    }

    const targetJobId = typeof jobState.jobId === "string" ? jobState.jobId.trim() : "";
    const response = await fetchWithSupabaseAuth(buildOptimizationCancelUrl(targetJobId), {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    });
    const payload = await parseJsonSafely(response);

    const payloadStatus = normalizeTrackedStatus(payload?.status, "");
    const isCancellationConflict = response.status === 409
      && isValidOptimizationStatus(payloadStatus)
      && isOptimizationStatusPayloadForJob(payload, targetJobId);

    if (!response.ok && !isCancellationConflict) {
      throw new Error(resolveTrackedMessage(
        payload,
        jobState.operationKind === "planning_reset"
          ? "Impossible d'arrêter la réinitialisation en cours."
        : "Impossible d'arrêter l'optimisation en cours.",
      ));
    }
    if (!isOptimizationStatusPayloadForJob(payload, targetJobId)) {
      throw new Error("La réponse d'annulation ne correspond plus au calcul suivi.");
    }

    const nextStatus = isCancellationConflict ? payloadStatus : payloadStatus || "cancelling";
    const nextState = buildOptimizationCancellationState(
      jobState,
      payload,
      nextStatus,
      { isConflict: isCancellationConflict },
    );
    setJobState((currentState) => {
      if (targetJobId && currentState.jobId !== targetJobId) {
        return currentState;
      }
      if (isCancellationConflict && currentState.status !== "running") {
        // A poll may have observed a newer cancelling/terminal transition
        // while this cancel request was in flight. Never regress that state
        // to the conflict payload's running snapshot.
        return currentState;
      }

      return buildOptimizationCancellationState(
        currentState,
        payload,
        nextStatus,
        { isConflict: isCancellationConflict },
      );
    });

    return nextState;
  }, [jobState]);

  const dismissOptimizationNotice = React.useCallback(() => {
    setJobState((currentState) => {
      if (currentState.status === "idle") {
        return currentState;
      }

      return {
        ...currentState,
        isDismissed: true,
      };
    });
  }, []);

  const updateOptimizationReferenceProbe = React.useCallback((patch) => {
    if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
      return;
    }

    setJobState((currentState) => ({
      ...currentState,
      referenceProbe: mergeOptimizationReferenceProbe(currentState.referenceProbe, patch),
    }));
  }, []);

  const clearOptimizationJob = React.useCallback(() => {
    setJobState(buildDefaultOptimizationJobState());
  }, []);

  const contextValue = React.useMemo(() => ({
    clearOptimizationJob,
    dismissOptimizationNotice,
    jobState,
    requestOptimizationCancel,
    planningResetLaunchScope,
    setPlanningResetLaunchScope,
    setOptimizationPollingEnabled,
    trackOptimizationLaunch,
    updateOptimizationReferenceProbe,
  }), [clearOptimizationJob, dismissOptimizationNotice, jobState, planningResetLaunchScope, requestOptimizationCancel, trackOptimizationLaunch, updateOptimizationReferenceProbe]);

  return React.createElement(
    OptimizationJobTrackerContext.Provider,
    { value: contextValue },
    children,
  );
}

export function useOptimizationJobTracker() {
  const context = React.useContext(OptimizationJobTrackerContext);
  if (!context) {
    throw new Error("useOptimizationJobTracker must be used within an OptimizationJobProvider.");
  }

  return context;
}
